import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  clinicalRulesApi,
  ClinicalRuleCreate,
  ClinicalRuleUpdate,
  RuleTestRequest,
  RuleApprovalRequest,
  RuleStatusUpdate,
  ClinicalRuleAlertStatusUpdate,
  RuleStatus,
  RuleType,
  RuleTrigger,
  RuleSeverity,
  AlertStatus,
} from '@services/clinical-rules.service'

export const useClinicalRules = () => {
  const queryClient = useQueryClient()

  const useList = (params?: { status?: RuleStatus; rule_type?: RuleType; trigger?: RuleTrigger }) =>
    useQuery({
      queryKey: ['clinical-rules', params],
      queryFn: () => clinicalRulesApi.list(params as any),
    })

  const useGet = (ruleId: string) =>
    useQuery({
      queryKey: ['clinical-rules', ruleId],
      queryFn: () => clinicalRulesApi.get(ruleId),
      enabled: !!ruleId,
    })

  const useFieldPaths = () =>
    useQuery({
      queryKey: ['clinical-rules', 'field-paths'],
      queryFn: () => clinicalRulesApi.getFieldPaths(),
    })

  const useAlerts = (params?: { patient_id?: string; rule_id?: string; status?: AlertStatus; severity?: RuleSeverity }) =>
    useQuery({
      queryKey: ['clinical-rules', 'alerts', params],
      queryFn: () => clinicalRulesApi.listAlerts(params as any),
    })

  const useAlert = (alertId: string) =>
    useQuery({
      queryKey: ['clinical-rules', 'alerts', alertId],
      queryFn: () => clinicalRulesApi.getAlert(alertId),
      enabled: !!alertId,
    })

  const useDashboardSummary = () =>
    useQuery({
      queryKey: ['clinical-rules', 'dashboard'],
      queryFn: () => clinicalRulesApi.getDashboardSummary(),
    })

  const create = useMutation({
    mutationFn: (data: ClinicalRuleCreate) => clinicalRulesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinical-rules'] })
    },
  })

  const update = useMutation({
    mutationFn: ({ ruleId, data }: { ruleId: string; data: ClinicalRuleUpdate }) =>
      clinicalRulesApi.update(ruleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinical-rules'] })
    },
  })

  const remove = useMutation({
    mutationFn: (ruleId: string) => clinicalRulesApi.delete(ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinical-rules'] })
    },
  })

  const approve = useMutation({
    mutationFn: ({ ruleId, data }: { ruleId: string; data: RuleApprovalRequest }) =>
      clinicalRulesApi.approve(ruleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinical-rules'] })
    },
  })

  const updateStatus = useMutation({
    mutationFn: ({ ruleId, data }: { ruleId: string; data: RuleStatusUpdate }) =>
      clinicalRulesApi.updateStatus(ruleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinical-rules'] })
    },
  })

  const test = useMutation({
    mutationFn: ({ ruleId, data }: { ruleId: string; data: RuleTestRequest }) =>
      clinicalRulesApi.test(ruleId, data),
  })

  const updateAlertStatus = useMutation({
    mutationFn: ({ alertId, data }: { alertId: string; data: ClinicalRuleAlertStatusUpdate }) =>
      clinicalRulesApi.updateAlertStatus(alertId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinical-rules', 'alerts'] })
    },
  })

  return {
    useList,
    useGet,
    useFieldPaths,
    useAlerts,
    useAlert,
    useDashboardSummary,
    create,
    update,
    remove,
    approve,
    updateStatus,
    test,
    updateAlertStatus,
  }
}
